import config from '../../Config';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { ValueProvider } from '@teliaee/gravity-forms';
import {
  Button,
  Collapse,
  CollapseProvider,
  CollapseToggle,
  CollapseTrigger,
  Heading,
  Modal,
  ModalContent,
  Separator,
  Text,
  AnalyticsTrigger,
} from 'telia-front-react';
import GravityForm from '../../forms/GravityForm';
import MobileBusinessStore, { NumberCount } from '../../stores/MobileBusinessStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import { GridProps, Package, PackageOptionGroup } from '../MobileBusinessInterfaces';
import CalculationTable from './CalculationTable';
import NumberCountSelector from './NumberCountSelector';
import PackageOptionCardGroups from './PackageOptionCardGroups';
import TotalPriceTable from './TotalPriceTable';

interface OrderFormProps {
  formName: string;
  packageFieldId: number;
  totalPriceFieldId: number;
  packageOptionGroups: PackageOptionGroup[];
  gridProps?: GridProps;
  mobileBusinessStore?: MobileBusinessStore;
}

const composeFlowUrl = (packageCode: string, dataSepv: number) => {
  const url = `${config.teliaWebSelfServiceUrl}/mobiil/liitumine`;

  if (packageCode && dataSepv) {
    return url.concat(`?packageCode=${packageCode}&dataSepv=${dataSepv}`);
  }
  return url;
};

@observer
class OrderForm extends React.Component<OrderFormProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  public componentDidMount() {
    this.initializePackageOptionMap();
  }

  public componentDidUpdate() {
    this.initializePackageOptionMap();
  }

  private initializePackageOptionMap() {
    const { mobileBusinessStore, packageOptionGroups } = this.props;
    mobileBusinessStore.initializePackageOptionMap(packageOptionGroups);
  }

  private closeForm() {
    this.props.mobileBusinessStore.clearPackageSelection();
  }

  private onSuccess() {
    this.props.mobileBusinessStore.setSubmitted(true);
  }

  get fieldValueProviders(): ValueProvider[] {
    const {
      packageFieldId,
      totalPriceFieldId,
      mobileBusinessStore: { visiblePackageOptionGroups },
    } = this.props;
    return visiblePackageOptionGroups
      .map((group) => ({
        fieldId: group.fieldId,
        data: `mobileBusiness_${group.name}`,
      }))
      .concat({ fieldId: packageFieldId, data: 'mobileBusiness_package' }, { fieldId: totalPriceFieldId, data: 'mobileBusiness_total' });
  }

  private renderFormOnlyVariant() {
    const translate = this.context.translate;
    const {
      formName,
      mobileBusinessStore: { gaChoicesString, selectedPackage, submitted, getCmsData },
    } = this.props;

    return (
      <>
        {!submitted && (
          <>
            <p>{translate('mobileBusiness.form.formOnlyInfo')}</p>
            <p className="text-small">{translate('mobileBusiness.form.selectedPackage')}</p>
            <span>{selectedPackage.capacity}</span>
            <Separator color="invisible" spacing="xsmall" />
          </>
        )}
        <GravityForm
          name={formName}
          fieldValueProviders={this.fieldValueProviders}
          gridColumnProps={{ width: ['xs-12'] }}
          buttonGridProps={{
            reverse: ['sm'],
            align: ['end-sm', 'middle-sm'],
          }}
          buttonGridColumnProps={{ width: ['xs-12', 'sm'] }}
          submitButtonProps={{
            block: true,
            type: 'primary',
          }}
          submitSuccessCallback={() => {
            this.onSuccess();
            return Promise.resolve();
          }}
          submissionCustomGaData={{ eventLabel: gaChoicesString }}
          cmsData={getCmsData(translate)}
        />
      </>
    );
  }

  private renderMultipleNumberForm() {
    const translate = this.context.translate;
    const {
      formName,
      mobileBusinessStore: { visiblePackageOptionGroups, totalPrice, selectedPackage, selectedPackageOptions, gaChoicesString, getCmsData },
    } = this.props;

    return (
      <>
        <Separator color="invisible" spacing="xsmall" />
        <TotalPriceTable totalPrice={totalPrice} />
        <Separator weight="thin" />

        {!!visiblePackageOptionGroups?.length && (
          <CollapseProvider id="calculation-collapse-1">
            <div className="text-right">
              <CollapseTrigger>
                <CollapseToggle text={translate('mobileBusiness.form.calculationProcess')} link={true} size="small" iconRight="#arrow-down" />
              </CollapseTrigger>
            </div>
            <Collapse noContainer={true}>
              <CalculationTable {...{ selectedPackage, selectedPackageOptions }} />
            </Collapse>
          </CollapseProvider>
        )}

        <GravityForm
          name={formName}
          fieldValueProviders={this.fieldValueProviders}
          gridColumnProps={{ width: ['xs-12', 'md-8', 'xl-7'] }}
          buttonGridProps={{
            reverse: ['sm'],
            align: ['end-sm', 'middle-sm'],
          }}
          buttonGridColumnProps={{ width: ['xs-12', 'sm'] }}
          submitButtonProps={{
            iconRight: '#arrow-right',
            block: true,
            type: 'primary',
          }}
          submitSuccessCallback={() => {
            this.onSuccess();
            return Promise.resolve();
          }}
          submissionCustomGaData={{ eventLabel: gaChoicesString }}
          cmsData={getCmsData(translate)}
        />
      </>
    );
  }

  private renderSingleNumberButton(mobilePackage: Package) {
    const translate = this.context.translate;
    const { packageCode, dataSepv } = mobilePackage;
    return (
      <>
        <Separator color="invisible" spacing="xxsmall" />
        <AnalyticsTrigger trigger={{ id: 'CTA', callback: 'onClick' }}>
          <Button text={translate('mobileBusiness.multiNumber.continue')} iconRight="#arrow-right" url={composeFlowUrl(packageCode, dataSepv)} />
        </AnalyticsTrigger>
      </>
    );
  }

  private getForm(numberCount: NumberCount, selectedPackage: Package) {
    if (selectedPackage.formOnly) {
      return this.renderFormOnlyVariant();
    }
    return numberCount === NumberCount.MULTIPLE ? this.renderMultipleNumberForm() : this.renderSingleNumberButton(selectedPackage);
  }

  public render() {
    const translate = this.context.translate;
    const {
      gridProps,
      mobileBusinessStore: { numberCount, visiblePackageOptionGroups, selectedPackage, submitted },
    } = this.props;
    const isFormOnly = selectedPackage?.formOnly;

    return selectedPackage ? (
      <Modal
        isOpen={!!selectedPackage}
        onClose={() => this.closeForm()}
        hasContentComponent={true}
        verticalAlign={isFormOnly ? 'middle' : null}
        size={isFormOnly ? '6-col' : '8-col'}
      >
        <ModalContent>
          <Text>
            {!submitted && (
              <>
                <Heading className="h3">{translate('mobileBusiness.form.header')}</Heading>
                {!isFormOnly && (
                  <>
                    <Separator color="invisible" spacing="xsmall" />
                    <p>{translate('mobileBusiness.multiNumber.info')}</p>
                    <Separator color="invisible" spacing="xsmall" />
                    <Heading level={2} className="sr-only">
                      {translate('mobileBusiness.multiNumber.chooseNumberCount')}
                    </Heading>
                    <NumberCountSelector selectedPackage={selectedPackage} />
                  </>
                )}

                {!!visiblePackageOptionGroups?.length && !isFormOnly && (
                  <>
                    <Separator color="invisible" spacing="xsmall" />
                    <PackageOptionCardGroups packageOptionGroups={visiblePackageOptionGroups} gridProps={gridProps} />
                  </>
                )}
              </>
            )}

            {this.getForm(numberCount, selectedPackage)}
          </Text>
        </ModalContent>
      </Modal>
    ) : null;
  }
}

export default inject('mobileBusinessStore')(OrderForm);
